import type { OrgRole, UserSummary } from "../dtos/User";

// Roles that exist in the roles list but are not offered for organisation members:
// Employee is the employee-portal role and changes where the user lands after login.
export const NON_ASSIGNABLE_ROLES: readonly string[] = ["SuperAdmin", "Employee"];

export function getUserRoleNames(user: Pick<UserSummary, "roleNames" | "roleName">): string[] {
  if (user.roleNames && user.roleNames.length > 0) return user.roleNames;
  return user.roleName ? [user.roleName] : [];
}

export function getUserRoleIds(user: Pick<UserSummary, "roleIds" | "roleId">): string[] {
  if (user.roleIds && user.roleIds.length > 0) return user.roleIds;
  return user.roleId ? [user.roleId] : [];
}

export const ORGANIZATION_OWNER_ROLE = "OrganizationOwner";
export const TEAM_MEMBER_ROLE = "TeamMember";

// Keys are plugin family ids; must match ProTalent.Application.Constants.OrganizationRoleCatalog.
export const MODULE_LABELS: Record<string, string> = {
  "salary-increase": "Salary Increases",
};

export interface ModuleRoleGroup {
  moduleKey: string;
  label: string;
  installed: boolean;
  roles: OrgRole[];
}

export interface AssignableRoles {
  baseRoles: OrgRole[];
  moduleGroups: ModuleRoleGroup[];
}

// Base roles are one-of; module roles are only offered while the add-on is active, or kept visible
// when the user already holds one so it can still be removed.
export function splitAssignableRoles(
  roles: OrgRole[],
  heldRoleIds: string[],
  activeModules: string[],
): AssignableRoles {
  const baseRoles = roles.filter(
    (role) =>
      role.isBaseRole &&
      (!NON_ASSIGNABLE_ROLES.includes(role.name) || heldRoleIds.includes(role.id)),
  );

  const groups = new Map<string, ModuleRoleGroup>();
  for (const role of roles) {
    if (!role.moduleKey) continue;
    const group = groups.get(role.moduleKey) ?? {
      moduleKey: role.moduleKey,
      label: MODULE_LABELS[role.moduleKey] ?? role.moduleKey,
      installed: activeModules.includes(role.moduleKey),
      roles: [],
    };
    group.roles.push(role);
    groups.set(role.moduleKey, group);
  }

  const moduleGroups = [...groups.values()].filter(
    (group) => group.installed || group.roles.some((role) => heldRoleIds.includes(role.id)),
  );

  return { baseRoles, moduleGroups };
}

// Users saved before the one-base-role rule may hold two; keep the more privileged one.
export function splitHeldRoles(
  roles: OrgRole[],
  heldRoleIds: string[],
): { baseRoleId: string; moduleRoleIds: string[] } {
  const held = roles.filter((role) => heldRoleIds.includes(role.id));
  const heldBase = held.filter((role) => role.isBaseRole);
  const base = heldBase.find((role) => role.name === ORGANIZATION_OWNER_ROLE) ?? heldBase[0];

  return {
    baseRoleId: base?.id ?? "",
    moduleRoleIds: held.filter((role) => role.moduleKey).map((role) => role.id),
  };
}
